
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Group, Channel, GroupMember } from "@/lib/types";
import { toast } from "sonner";
import { useGroupActions } from "./groups/use-group-actions";

export const useGroupDetails = (groupId?: string, userId?: string) => {
  const [group, setGroup] = useState<Group | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [memberCount, setMemberCount] = useState(0);
  const [currentMember, setCurrentMember] = useState<GroupMember | null>(null);
  const [loading, setLoading] = useState(true);

  const { joinGroup, leaveGroup } = useGroupActions(groupId || "", userId);

  const handleJoinGroup = async () => {
    const success = await joinGroup();
    if (success) {
      // Refresh data
      fetchGroupData();
    }
    return success;
  };

  const handleLeaveGroup = async () => {
    const success = await leaveGroup();
    if (success) {
      // Refresh data
      fetchGroupData();
    }
    return success;
  };

  const handleCreateChannel = async (channelData: { 
    name: string; 
    description: string; 
    isAnnouncement: boolean; 
    isTemporary: boolean; 
    autoDeleteAfter?: number 
  }) => {
    if (!groupId || !userId) return false;

    try {
      const { error } = await supabase
        .from("channels")
        .insert({
          name: channelData.name,
          description: channelData.description,
          is_announcement: channelData.isAnnouncement,
          is_temporary: channelData.isTemporary,
          auto_delete_after: channelData.autoDeleteAfter,
          group_id: groupId,
          created_by: userId
        });

      if (error) throw error;

      toast.success("Channel created successfully!");
      fetchGroupData(); // Refresh channels
      return true;
    } catch (error: any) {
      console.error("Error creating channel:", error);
      toast.error(error.message || "Failed to create channel");
      return false;
    }
  };

  const handleUpdateGroup = async (updatedGroup: { 
    name: string; 
    description?: string; 
    rules?: string 
  }) => {
    if (!groupId) return false;

    try {
      const { error } = await supabase
        .from("groups")
        .update({
          name: updatedGroup.name,
          description: updatedGroup.description,
          rules: updatedGroup.rules
        })
        .eq("id", groupId);

      if (error) throw error;

      toast.success("Group updated successfully!");
      fetchGroupData(); // Refresh group data
      return true;
    } catch (error: any) {
      console.error("Error updating group:", error);
      toast.error(error.message || "Failed to update group");
      return false;
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupId) return false;

    try {
      const { error } = await supabase
        .from("groups")
        .delete()
        .eq("id", groupId);

      if (error) throw error;

      toast.success("Group deleted successfully!");
      return true;
    } catch (error: any) {
      console.error("Error deleting group:", error);
      toast.error(error.message || "Failed to delete group");
      return false;
    }
  };

  const handleShareGroup = (group: Group) => {
    const url = `${window.location.origin}/groups/${group.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Group link copied to clipboard!");
  };

  const fetchGroupData = async () => {
    if (!groupId) {
      setLoading(false);
      return;
    }

    try {
      // Fetch group details
      const { data: groupData, error: groupError } = await supabase
        .from("groups")
        .select("*")
        .eq("id", groupId)
        .single();

      if (groupError) throw groupError;

      if (groupData) {
        setGroup({
          id: groupData.id,
          name: groupData.name,
          description: groupData.description,
          rules: groupData.rules,
          createdAt: groupData.created_at,
          createdBy: groupData.created_by,
          updatedAt: groupData.updated_at,
        });
      }

      // Fetch channels
      const { data: channelsData, error: channelsError } = await supabase
        .from("channels")
        .select("*")
        .eq("group_id", groupId)
        .order("created_at", { ascending: true });

      if (channelsError) throw channelsError;

      if (channelsData) {
        const formattedChannels: Channel[] = channelsData.map(channel => ({
          id: channel.id,
          name: channel.name,
          description: channel.description || "",
          isAnnouncement: channel.is_announcement || false,
          isTemporary: channel.is_temporary || false,
          autoDeleteAfter: channel.auto_delete_after,
          groupId: channel.group_id,
          createdAt: channel.created_at,
          updatedAt: channel.updated_at,
          createdBy: channel.created_by,
        }));
        setChannels(formattedChannels);
      }

      // Fetch member count
      const { count, error: countError } = await supabase
        .from("group_members")
        .select("*", { count: "exact", head: true })
        .eq("group_id", groupId);

      if (countError) throw countError;
      setMemberCount(count || 0);

      // Fetch current user membership
      if (userId) {
        const { data: memberData, error: memberError } = await supabase
          .from("group_members")
          .select("*")
          .eq("group_id", groupId)
          .eq("user_id", userId)
          .single();

        if (memberError && memberError.code !== "PGRST116") {
          throw memberError;
        }

        if (memberData) {
          setCurrentMember({
            id: memberData.id,
            groupId: memberData.group_id,
            userId: memberData.user_id,
            role: memberData.role,
            isBanned: memberData.is_banned,
            isMuted: memberData.is_muted,
            mutedUntil: memberData.muted_until,
            joinedAt: memberData.joined_at,
          });
        } else {
          setCurrentMember(null);
        }
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
      toast.error("Failed to load group details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupData();
  }, [groupId, userId]);

  return {
    group,
    channels,
    memberCount,
    currentMember,
    loading,
    handleJoinGroup,
    handleLeaveGroup,
    handleCreateChannel,
    handleUpdateGroup,
    handleDeleteGroup,
    handleShareGroup
  };
};
